import React, { useState, useEffect } from "react";
import { Phone, X, User } from "lucide-react";
import { apiClient } from "../utils/apiClient";

/**
 * ManagerContactWidget
 * Displays manager contact information with a call button
 * Fetches manager number based on location and city from URL parameters
 */
const ManagerContactWidget = ({ isOpen, onClose, location, city }) => {
  const [managerData, setManagerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && location && city) {
      fetchManagerContact();
    }
  }, [isOpen, location, city]);

  const fetchManagerContact = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get("/api/manager-contact", {
        params: { location, city },
      });

      if (response.data.success) {
        setManagerData(response.data.data);
      } else {
        setError(response.data.error || "Failed to fetch manager contact");
      }
    } catch (err) {
      console.error("Failed to fetch manager contact:", err);
      setError(
        err?.response?.data?.error || 
        "Unable to fetch manager contact information. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCall = () => {
    if (managerData?.manager_number) {
      // For mobile devices, this will open the phone dialer
      window.location.href = `tel:${managerData.manager_number}`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-auto overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-3 sm:p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <User className="text-white" size={20} />
            <h2 className="text-white font-semibold text-base sm:text-lg">Contact Manager</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mb-3"></div>
              <p className="text-gray-600 text-sm">Loading...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          ) : managerData ? (
            <div className="space-y-3">
              {/* Store Information */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="font-semibold text-gray-800 text-base capitalize mb-1">
                  {managerData.store_name}
                </h3>
                <p className="text-xs text-gray-600 capitalize">
                  {managerData.city}, {managerData.state}
                </p>
              </div>

              {/* Manager Contact */}
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-600 mb-2 font-medium">Manager Contact</p>
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  <span className="font-semibold text-base sm:text-lg text-gray-800">
                    {managerData.manager_number}
                  </span>
                  <button
                    onClick={handleCall}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-full p-2 transition-all transform hover:scale-110 active:scale-95 shadow-md flex-shrink-0"
                    title="Call Manager"
                  >
                    <Phone size={18} />
                  </button>
                </div>
              </div>

              {/* Store Phone (if different) */}
              {managerData.phone_number && managerData.phone_number !== managerData.manager_number && (
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Store Contact</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                    <span className="text-sm text-gray-700 font-medium">
                      {managerData.phone_number}
                    </span>
                  </div>
                </div>
              )}

              {/* Store Email */}
              {managerData.email && (
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Email</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                    <a 
                      href={`mailto:${managerData.email}`}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline break-all"
                    >
                      {managerData.email}
                    </a>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ManagerContactWidget;

